import { Record } from 'immutable';
import * as ActionUnauthorizedReasons from 'automation-ui-action-features/access/constants/ActionUnauthorizedReasons';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import * as CRMObjectTypes from 'automation-ui-references/object-types/CRMObjectTypes';
import { MARKETING_PRO, SALES_PROFESSIONAL, SERVICE_PROFESSIONAL, MARKETING_ENTERPRISE, SALES_ENTERPRISE, SERVICE_ENTERPRISE, OPERATIONS_PROFESSIONAL } from 'self-service-api/constants/UpgradeProducts';
import { isCustomObject } from 'automation-ui-references/object-types/objectTypeUtils';
import enviro from 'enviro';
const DEFAULT_FOR_ANY_FLOW = '*';
export class PqlModalConfig extends Record({
  modalKey: null,
  upgradeProduct: null,
  useUMSConfig: false
}, 'PqlModalConfig') {}

/*
 * PRO CONFIGS
 */
const MarketingProModalConfig = new PqlModalConfig({
  modalKey: 'workflows-marketing-professional',
  upgradeProduct: MARKETING_PRO
});
const ServiceProModalConfig = new PqlModalConfig({
  modalKey: 'workflows-service-professional',
  upgradeProduct: SERVICE_PROFESSIONAL
});
const OperationsHubProModalConfig = new PqlModalConfig({
  modalKey: 'workflows-operations-pro',
  upgradeProduct: OPERATIONS_PROFESSIONAL,
  useUMSConfig: true
});

/*
 * ENTERPRISE CONFIGS
 */
const MarketingEntModalConfig = new PqlModalConfig({
  modalKey: 'workflows-marketing-enterprise',
  upgradeProduct: MARKETING_ENTERPRISE
});
const SalesEntModalConfig = new PqlModalConfig({
  modalKey: 'workflows-sales-enterprise',
  upgradeProduct: SALES_ENTERPRISE
});
const ServiceEntModalConfig = new PqlModalConfig({
  modalKey: 'workflows-service-enterprise',
  upgradeProduct: SERVICE_ENTERPRISE
});

/*
 * FLOW-LEVEL CONFIGS
 */

const CreateDealFlowConfig = new PqlModalConfig({
  modalKey: 'workflows-create-deal-flow',
  upgradeProduct: SALES_PROFESSIONAL
});
const CreateTicketFlowConfig = new PqlModalConfig({
  modalKey: 'workflows-create-ticket-flow',
  upgradeProduct: SERVICE_PROFESSIONAL
});
const CreateQuoteFlowConfig = new PqlModalConfig({
  modalKey: 'workflows-create-quote-flow',
  upgradeProduct: SALES_ENTERPRISE
});

/*
 * ACTION-LEVEL CONFIGS
 */
const FormulaActionModalConfig = new PqlModalConfig({
  modalKey: 'workflows-actions-formula',
  upgradeProduct: OPERATIONS_PROFESSIONAL,
  useUMSConfig: true
});
const SequencesActionsModalConfig = new PqlModalConfig({
  modalKey: 'workflows-actions-sequences',
  upgradeProduct: SALES_ENTERPRISE
});

/*
 * CONFIG MAPPINGS
 */
const FlowObjectTypeToConfig = {
  [CRMObjectTypes.CONTACT_ID]: MarketingProModalConfig,
  [CRMObjectTypes.COMPANY_ID]: MarketingProModalConfig,
  [CRMObjectTypes.DEAL_ID]: CreateDealFlowConfig,
  [CRMObjectTypes.TICKET_ID]: CreateTicketFlowConfig,
  [CRMObjectTypes.QUOTE_ID]: CreateQuoteFlowConfig,
  [CRMObjectTypes.FEEDBACK_SUBMISSION_ID]: ServiceProModalConfig
};

// TODO: As we move away from Classic, we should do away with this
// map. It makes an assumption that every upgrade path is to Pro.
// Instead, we should leverage ActionTypeToConfig, which gives us
// more granularity.
const ActionUnauthorizedReasonToDefaultConfig = {
  [ActionUnauthorizedReasons.MARKETING_ACCESS_DENIED]: MarketingProModalConfig,
  [ActionUnauthorizedReasons.SERVICE_ACCESS_DENIED]: ServiceProModalConfig,
  [ActionUnauthorizedReasons.ENTERPRISE_REQUIRED]: MarketingEntModalConfig
};
const ActionTypeToConfig = {
  [ActionTypes.EMAIL]: {
    [DEFAULT_FOR_ANY_FLOW]: MarketingProModalConfig
  },
  [ActionTypes.SLACK_NOTIFICATION]: {
    [DEFAULT_FOR_ANY_FLOW]: MarketingEntModalConfig,
    [CRMObjectTypes.DEAL_ID]: SalesEntModalConfig,
    [CRMObjectTypes.QUOTE_ID]: SalesEntModalConfig,
    [CRMObjectTypes.TICKET_ID]: ServiceEntModalConfig
  },
  [ActionTypes.WEBHOOK]: {
    [DEFAULT_FOR_ANY_FLOW]: OperationsHubProModalConfig
  },
  [ActionTypes.CUSTOM_CODE]: {
    [DEFAULT_FOR_ANY_FLOW]: OperationsHubProModalConfig
  },
  [ActionTypes.FORMULA]: {
    [DEFAULT_FOR_ANY_FLOW]: FormulaActionModalConfig
  },
  [ActionTypes.ALTER_LIST_MEMBERSHIP]: {
    [DEFAULT_FOR_ANY_FLOW]: MarketingProModalConfig
  },
  [ActionTypes.AB_TEST_BRANCH]: {
    [DEFAULT_FOR_ANY_FLOW]: MarketingProModalConfig
  }
};
export const getPqlConfigForFlowObjectTypeId = flowObjectTypeId => {
  // TODO: revisit what pqlConfig to return for different meta types (SEE)
  if (isCustomObject(flowObjectTypeId)) {
    return MarketingEntModalConfig;
  }
  return FlowObjectTypeToConfig[flowObjectTypeId] || null;
};
export const getPqlKeyForFlowObjectTypeId = flowObjectTypeId => {
  const pqlConfig = getPqlConfigForFlowObjectTypeId(flowObjectTypeId);
  return pqlConfig && pqlConfig.modalKey || undefined;
};
export const getPqlConfigForActionUnauthorizedReason = reason => ActionUnauthorizedReasonToDefaultConfig[reason] || null;
export const getPqlConfigForActionType = (flowObjectTypeId, actionType) => {
  // may be undefined, but should still be able to index array
  const configsForActionType = ActionTypeToConfig[actionType];
  if (!configsForActionType) {
    return null;
  }
  const defaultConfig = configsForActionType[DEFAULT_FOR_ANY_FLOW] || null;
  return configsForActionType[flowObjectTypeId] || defaultConfig;
};
export const getPqlKeyForActionType = (flowObjectTypeId, actionType) => {
  const pqlConfig = getPqlConfigForActionType(flowObjectTypeId, actionType);
  return pqlConfig && pqlConfig.modalKey || undefined;
};
export const getPqlConfigForSalesProWorkflowLimits = () => MarketingEntModalConfig;
export const getPqlConfigForCustomAction = actionTypeId => {
  if (enviro.isProd()) {
    switch (actionTypeId) {
      case '0-3174832': // Enroll in a sequence
      case '0-4702372':
        // Unenroll from sequence
        return SequencesActionsModalConfig;
      default:
        return undefined;
    }
  }
  switch (actionTypeId) {
    case '0-4404496': // Enroll in a sequence
    case '0-4404497':
      // Unenroll from sequence
      return SequencesActionsModalConfig;
    default:
      return undefined;
  }
};
export const getPqlKeyForCustomAction = actionTypeId => {
  const pqlConfig = getPqlConfigForCustomAction(actionTypeId);
  return pqlConfig && pqlConfig.modalKey || undefined;
};